<template>
  <!-- Sección 1: Banners corporativos a pantalla completa -->
  <div class="min-h-screen relative overflow-hidden" id="banners-section">
    <!-- Banners rotativos como fondo principal -->
    <div class="absolute inset-0 z-0">
      <div 
        v-for="(banner, index) in banners" 
        :key="index"
        class="absolute inset-0 transition-opacity duration-1000 bg-cover bg-center bg-no-repeat"
        :class="currentBanner === index ? 'opacity-100' : 'opacity-0'"
        :style="{ backgroundImage: `url('/banners/${banner}')` }"
      ></div>
      <!-- Overlay sutil para legibilidad -->
      <div class="absolute inset-0 bg-gradient-to-br from-black/20 via-transparent to-black/30"></div>
    </div>

    <!-- Botón Iniciar Sesión en esquina superior derecha -->
    <div class="absolute top-6 right-6 z-20">
      <button 
        @click="scrollToLogin"
        class="bg-white/90 hover:bg-white backdrop-blur-sm text-gray-800 font-bold py-3 px-6 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 border border-yellow-200"
      >
        Iniciar Sesión
      </button>
    </div>

    <!-- Logo en esquina superior izquierda -->
    <div class="absolute top-6 left-6 z-20" v-if="infoData.hasOwnProperty('logo')">
      <img class="h-12 md:h-16 w-auto" :src="isDevelopment ? currentLogo : infoData.logo" alt="MARBEL">
    </div>

    <!-- Contenido central -->
    <div class="relative min-h-screen flex items-center justify-center z-10">
      <!-- Contenido limpio sin texto para mostrar banners en todo su esplendor -->
    </div>

    <!-- Indicador de scroll -->
    <div class="absolute bottom-6 left-1/2 transform -translate-x-1/2 z-20">
      <button 
        @click="scrollToLogin"
        class="text-white/80 hover:text-white transition-colors animate-bounce"
      >
        <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
        </svg>
      </button>
    </div>
  </div>

  <!-- Sección 2: Formulario de Login -->
  <div class="min-h-screen bg-gradient-to-br from-amber-50 to-yellow-50 flex items-center justify-center py-12" id="login-section">
    <div class="container mx-auto px-4">
      <div class="max-w-6xl mx-auto">
        <div class="grid lg:grid-cols-2 gap-12 items-center">
          
          <!-- Información de la empresa -->
          <div class="text-center lg:text-left space-y-8">
            <!-- Logo -->
            <div class="flex justify-center lg:justify-start">
              <div class="relative" v-if="infoData.hasOwnProperty('logo')">
                <img class="h-16 md:h-20 w-auto" :src="isDevelopment ? currentLogo : infoData.logo" alt="MARBEL">
                <span v-if="isDevelopment" class="absolute -top-1 -right-1 bg-red-500 text-white text-xs px-1 rounded" style="font-size: 8px;">DEV</span>
              </div>
            </div>

            <!-- Información corporativa -->
            <div>
              <h2 class="text-3xl lg:text-4xl font-bold text-gray-800 leading-tight mb-4">
                Bienvenido a<br>
                <span class="text-transparent bg-clip-text bg-gradient-to-r from-yellow-600 to-amber-600">
                  MARBEL
                </span>
              </h2>
              <p class="text-lg text-gray-600 mb-6">
                Accede a tu cuenta para gestionar proyectos, revisar cotizaciones y administrar la producción de cortinas y persianas de la más alta calidad.
              </p>
            </div>

            <!-- Services Grid -->
            <div class="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div class="bg-white/70 backdrop-blur-sm rounded-lg p-4 text-center hover:bg-white/90 transition-all duration-300 hover:scale-105">
                <div class="text-2xl mb-2">🪟</div>
                <div class="text-sm font-medium text-gray-700">Cortinas</div>
              </div>
              <div class="bg-white/70 backdrop-blur-sm rounded-lg p-4 text-center hover:bg-white/90 transition-all duration-300 hover:scale-105">
                <div class="text-2xl mb-2">🏠</div>
                <div class="text-sm font-medium text-gray-700">Persianas</div>
              </div>
              <div class="bg-white/70 backdrop-blur-sm rounded-lg p-4 text-center hover:bg-white/90 transition-all duration-300 hover:scale-105">
                <div class="text-2xl mb-2">🎨</div>
                <div class="text-sm font-medium text-gray-700">Papel Tapiz</div>
              </div>
              <div class="bg-white/70 backdrop-blur-sm rounded-lg p-4 text-center hover:bg-white/90 transition-all duration-300 hover:scale-105">
                <div class="text-2xl mb-2">🤖</div>
                <div class="text-sm font-medium text-gray-700">Automatización</div>
              </div>
              <div class="bg-white/70 backdrop-blur-sm rounded-lg p-4 text-center hover:bg-white/90 transition-all duration-300 hover:scale-105">
                <div class="text-2xl mb-2">☂️</div>
                <div class="text-sm font-medium text-gray-700">Toldos</div>
              </div>
              <div class="bg-white/70 backdrop-blur-sm rounded-lg p-4 text-center hover:bg-white/90 transition-all duration-300 hover:scale-105">
                <div class="text-2xl mb-2">✨</div>
                <div class="text-sm font-medium text-gray-700">Lambrequines</div>
              </div>
            </div>
          </div>

          <!-- Login Form Side -->
          <div class="flex justify-center lg:justify-end">
            <div class="w-full max-w-md">
              <div class="bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl p-8 border border-yellow-200 ring-1 ring-white/50">
                <!-- Form Header -->
                <div class="text-center mb-8">
                  <h3 class="text-2xl font-bold text-gray-800 mb-2">Iniciar Sesión</h3>
                  <p class="text-gray-600">Accede a tu cuenta</p>
                </div>

                <!-- Login Form -->
                <form @submit.prevent="ingresar" class="space-y-6">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">Usuario</label>
                    <input 
                      v-model="usuario" 
                      type="text" 
                      required
                      class="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-yellow-400 focus:ring-2 focus:ring-yellow-200 transition-colors outline-none bg-white/95 shadow-sm"
                      placeholder="Ingresa tu usuario"
                    >
                  </div>
                  
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">Contraseña</label>
                    <input 
                      v-model="contrasena" 
                      type="password" 
                      required
                      class="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-yellow-400 focus:ring-2 focus:ring-yellow-200 transition-colors outline-none bg-white/95 shadow-sm"
                      placeholder="Ingresa tu contraseña"
                    >
                  </div>

                  <button 
                    type="submit"
                    class="w-full bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-600 hover:to-amber-600 text-white font-bold py-3 px-4 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
                  >
                    Ingresar
                  </button>
                </form>

                <!-- Experience Badge -->
                <div class="mt-6 text-center">
                  <div class="inline-flex items-center gap-2 bg-gradient-to-r from-yellow-100 to-amber-100 text-yellow-800 px-4 py-2 rounded-full text-sm font-medium">
                    <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                    </svg>
                    18+ años de experiencia
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { logoMixin } from '@/mixins/logoMixin';
import {
    ref,
    onMounted,
    onUnmounted
} from "vue";
import userService from "@/Services/user";
import infosistem from "@/Services/infosistem";

import {
    useUserStore
} from '../stores/user'
import {
    useInfoStore
} from '../stores/info'
import {
    useRouter,
    useRoute
} from 'vue-router'

export default {
    mixins: [logoMixin],
    props: {
        price: {
            type: Number,
            default: 0
        },
    },
    setup() {
        const router = useRouter()
        const route = useRoute()

        const infoData = ref({});
        const info = useInfoStore()
        const user = useUserStore()

        const usuario = ref('');
        const contrasena = ref('');

        // Banners rotativos
        const banners = ref([
            'BHAG.jpg',
            'CORE VALUES_1.jpg',
            'CORE VALUES_2.jpg', 
            'CORE VALUES_3.jpg',
            'CORE VALUES_4.jpg',
            'CORE VALUES_5.jpg',
            'CORE VALUES_6.jpg',
            'PROMESAS DE MARCA_01.jpg',
            'PROMESAS-DE-MARCA_02.jpg',
            'PROMESAS-DE-MARCA_03.jpg',
            'PROPÓSITO.jpg'
        ]);
        
        const currentBanner = ref(0);
        let bannerInterval = null;

        // Rotación automática de banners
        const startBannerRotation = () => {
            console.log('Iniciando rotación de banners');
            bannerInterval = setInterval(() => {
                currentBanner.value = (currentBanner.value + 1) % banners.value.length;
                console.log('Banner actual:', currentBanner.value, 'Banner:', banners.value[currentBanner.value]);
            }, 6000); // Cambiar cada 6 segundos
        };

        const stopBannerRotation = () => {
            if (bannerInterval) {
                clearInterval(bannerInterval);
                bannerInterval = null;
            }
        };

        // Función para scroll suave al formulario
        const scrollToLogin = () => {
            document.getElementById('login-section').scrollIntoView({
                behavior: 'smooth'
            });
        };

        // Iniciar rotación inmediatamente
        startBannerRotation();

        const ingresar = async () => {
            if (usuario.value === '') {
                alert("Campo usuario es obligatorio");
                return;
            }
            if (contrasena.value === '') {
                alert("Campo contraseña es obligatorio");
                return;
            }

            let dat = {
                phone: usuario.value,
                password: contrasena.value
            };

            try {
                let response = await userService.LOGIN_USER(dat);
                
                if (response != null && !response.hasOwnProperty('error')) {
                    user.set(response);

                    if (response.user.role != 'cliente') {
                        router.push('/app/welcome');
                    } else {
                        router.push('/cl');
                    }
                } else {
                    alert("Usuario o contraseña incorrectos");
                }
            } catch (error) {
                alert("Ocurrió un error al intentar ingresar");
                console.error('Login error:', error);
            }
        }

        const info_get = async () => {
            if (route.query.hasOwnProperty('cp')) {
                usuario.value = (route.query.cp).split('***')[1];
                contrasena.value = (route.query.cp).split('***')[0];
            }

            try {
                let response = await infosistem.GET();
                if (response != null && !response.hasOwnProperty('error')) {
                    let infov = {};
                    response.forEach(element => {
                        infov[element.id] = element.valor;
                    });
                    info.set(infov);
                    infoData.value = infov;
                }
            } catch (error) {
                console.error('Info get error:', error);
            }
        }

        info_get();

        // Banner rotation lifecycle        
        onMounted(() => {
            startBannerRotation();
        });

        onUnmounted(() => {
            if (bannerInterval) {
                clearInterval(bannerInterval);
            }
        });

        return {
            ingresar,
            usuario,
            contrasena,
            user,
            router,
            route,
            infoData,
            banners,
            currentBanner,
            scrollToLogin
        };
    }
};
</script>

<style scoped>
/* Animaciones personalizadas para la marca MARBEL */
.hover\:grow {
    transition: all 0.3s ease;
    transform: scale(1);
}

.hover\:grow:hover {
    transform: scale(1.02);
}

/* Gradientes suaves para el fondo */
.bg-gradient-brand {
    background: linear-gradient(135deg, #fef3c7 0%, #fbbf24 50%, #f59e0b 100%);
}

/* Efectos de glassmorphism */
.backdrop-blur-brand {
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
}

/* Animación suave para los elementos */
@keyframes fadeInUp {
    from {
        opacity: 0;
        transform: translateY(20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.animate-fadeInUp {
    animation: fadeInUp 0.8s ease-out;
}

/* Scroll suave */
html {
    scroll-behavior: smooth;
}
</style>
